import React, { useState, useEffect, useRef, useContext } from "react"

import { useHistory, useParams } from "react-router-dom"
import { parseISO, format, isSameDay } from "date-fns"
import clsx from "clsx"

import { makeStyles } from "@mui/styles"
import { green } from "@mui/material/colors"
import ListItem from "@mui/material/ListItem"
import ListItemText from "@mui/material/ListItemText"
import ListItemAvatar from "@mui/material/ListItemAvatar"
import Typography from "@mui/material/Typography"
import Avatar from "@mui/material/Avatar"
import Divider from "@mui/material/Divider"
import Badge from "@mui/material/Badge"

import CheckIcon from "@mui/icons-material/CheckCircle"

import api from "../../services/api"
import MarkdownWrapper from "../MarkdownWrapper"
import { Box, IconButton, Tooltip } from "@mui/material"
import { AuthContext } from "../../context/Auth/AuthContext"
import toastError from "../../errors/toastError"

const useStyles = makeStyles(() => ({
    ticket: {
        position: "relative",
        cursor: "pointer",
    },

    pendingTicket: {
        cursor: "default",
    },

    badgeStyle: {
        color: "white",
        backgroundColor: green[500],
    },

    ticketQueueColor: {
        flex: "none",
        width: "8px",
        height: "100%",
        position: "absolute",
        top: "0%",
        left: "0%",
    },
}))

const TicketListItem = ({ ticket, onTicketClick }) => {
    const classes = useStyles()
    const history = useHistory()
    const [loading, setLoading] = useState(false)
    const { ticketId } = useParams()
    const isMounted = useRef(true)
    const { user } = useContext(AuthContext)

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    const handleAcepptTicket = async (ticket) => {
        setLoading(true)
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id,
            })
        } catch (err) {
            setLoading(false)
            toastError(err)
        }
        if (isMounted.current) {
            setLoading(false)
        }

        onTicketClick(ticket.id)

        history.push(`/tickets/${ticket.uuid}`)
    }

    const handleSelectTicket = (ticket) => {
        history.push(`/tickets/${ticket.uuid}`)
    }

    return (
        <React.Fragment key={ticket.id}>
            <ListItem
                dense
                onClick={() => {
                    if (ticket.status === "pending") return
                    handleSelectTicket(ticket)
                    onTicketClick(ticket.id)
                }}
                selected={ticketId && +ticketId === ticket.id}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                })}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    gap={1}
                >
                    <Box flexBasis="90%" display="flex" alignItems="center">
                        <Tooltip
                            arrow
                            placement="right"
                            title={ticket.queue?.name || "Sem fila"}
                        >
                            <span
                                style={{
                                    backgroundColor:
                                        ticket.queue?.color || "#7C7C7C",
                                }}
                                className={classes.ticketQueueColor}
                            ></span>
                        </Tooltip>
                        <ListItemAvatar sx={{ position: "relative" }}>
                            <Avatar src={ticket?.contact?.profilePicUrl} />
                            <Box position="absolute" bottom={-10} right={20}>
                                <Badge
                                    badgeContent={ticket.unreadMessages}
                                    classes={{
                                        badge: classes.badgeStyle,
                                    }}
                                />
                            </Box>
                        </ListItemAvatar>

                        <Box width="100%">
                            <ListItemText
                                disableTypography
                                primary={
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        width="100%"
                                    >
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            color="textPrimary"
                                        >
                                            {ticket.contact.name}
                                        </Typography>
                                        {ticket.status === "closed" && (
                                            <Badge
                                                badgeContent={"closed"}
                                                color="primary"
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Box>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {ticket.lastMessage ? (
                                                <MarkdownWrapper>
                                                    {ticket.lastMessage}
                                                </MarkdownWrapper>
                                            ) : (
                                                <br />
                                            )}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>

                    <Box
                        flexBasis="10%"
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                    >
                        {ticket.lastMessage && (
                            <Typography
                                component="span"
                                variant="body2"
                                color="textSecondary"
                            >
                                {isSameDay(
                                    parseISO(ticket.updatedAt),
                                    new Date(),
                                ) ? (
                                    <>
                                        {format(
                                            parseISO(ticket.updatedAt),
                                            "HH:mm",
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {format(
                                            parseISO(ticket.updatedAt),
                                            "dd/MM/yyyy",
                                        )}
                                    </>
                                )}
                            </Typography>
                        )}
                        {ticket.status === "pending" && (
                            <Tooltip title="Aceitar">
                                <IconButton
                                    loading={loading}
                                    className={classes.acceptButton}
                                    onClick={() => handleAcepptTicket(ticket)}
                                    color="primary"
                                >
                                    <CheckIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </ListItem>
            <Divider variant="inset" component="li" />
        </React.Fragment>
    )
}

export default TicketListItem
